import UCrypto from "@repo/utils/UCrypto";

import appSettings from "~/appSettings";
import { useUser } from "~/state/features/user";
import Marketing from "~/utils/Marketing";

import { AnalyticsEvents, Registration } from "../../client";

declare global {
  interface Window {
    ttq: any;
  }
}

const id = appSettings.marketing.tiktokId;

export default (): Registration.Conversion => {
  if (!id) return;
  if (import.meta.env.APP_ENV !== "production") return;

  // prettier-ignore
  // @ts-expect-error
  // eslint-disable-next-line
  !function (w, d, t) {w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};}(window, document, 'ttq');

  window.ttq.load(id);
  window.ttq.page();

  async function useIdentifyParams() {
    const [user] = useUser();

    const [emailHash, phoneHash] = await Promise.all([
      UCrypto.sha256Hex(user.pii()?.email),
      // Requires E164 format: +{country code}{phone number}
      UCrypto.sha256Hex(`+${user.pii()?.phone}`),
    ]);

    return {
      external_id: user.id(),
      email: emailHash,
      phone_number: phoneHash,
    };
  }

  // https://ads.tiktok.com/marketing_api/docs?rid=5ipocbxyw8v&id=1701890973258754
  const eventByName: Partial<
    Record<
      keyof AnalyticsEvents["conversion"],
      | "AddPaymentInfo"
      | "AddToCart"
      | "AddToWishlist"
      | "ApplicationApproval"
      | "Purchase"
      | "CompleteRegistration"
      | "Contact"
      | "CustomizeProduct"
      | "Download"
      | "FindLocation"
      | "InitiateCheckout"
      | "Lead"
      | "Schedule"
      | "Search"
      | "StartTrial"
      | "SubmitApplication"
      | "Subscribe"
      | "ViewContent"
      // No longer documented
      | "ClickButton"
      | "CompletePayment"
      | "PlaceAnOrder"
      | "SubmitForm"
    >
  > = {
    "Credit Account Open": "CompletePayment",
    "Make Credit Line Payment": "Subscribe",
  };

  return {
    async identify() {
      const identifyParams = useIdentifyParams();

      window.ttq.identify(await identifyParams);
    },
    async track(name, properties) {
      const event = eventByName[name];
      if (!event) return;

      const identifyParams = useIdentifyParams();
      const expectedLtv = Marketing.useExpectedLtv(properties);
      const accountId = properties.accountId;

      const payload = {
        currency: appSettings.currency,
        value: expectedLtv,
        content_type: "product",
        contents: [{ content_id: accountId }],
      };

      window.ttq.track(event, payload, { event_id: accountId });

      sendServerEvent({
        pixel_code: id,
        event_id: accountId,
        event,
        context: {
          user: await identifyParams,
          page: { url: window.location.href },
        },
        properties: payload,
      });
    },
  };
};

async function sendServerEvent(params: any) {
  "use server";

  await fetch("https://business-api.tiktok.com/open_api/v1.3/pixel/track/", {
    method: "POST",
    body: JSON.stringify(params),
    headers: {
      "Access-Token": "b72d08e6b8b13ad7ae950acb7547a20614b312c9",
      "Content-Type": "application/json",
    },
  });
}
